/* ============================================================
   姿態基準、腿部 IK、彈簧（1:1 移植 `mech_sample_ani.html` §4b / §9）
   ============================================================

   「呆板」的成因是六個部位掛在同一條 Y 軸上：髖線、肩線、視線全部平行。
   AC 的機庫姿勢是**重心偏在一腳、髖線與肩線交叉、頭獨立追瞄**。
   腳掌角一律 = -(髖+膝)，所以無論怎麼擺都貼地。 */

/** 站立基準姿態。動畫層會覆蓋大部分，但這是所有偏移的原點。 */
export var POSE = {
  legL: { hip: -0.30, knee: 0.66 },   // 左＝支撐腿，較直
  legR: { hip: -0.52, knee: 0.94 },   // 右＝前踏腿，折得深
  hipYaw: 0.20,               // 髖線轉 11°，軀幹不轉 → 兩線交叉
  /* **不要手調** —— 它是 atan(兩髖高低差 / 站姿寬) 的結果，動起來會自己算。 */
  pelvisTilt: 0.115,
  torsoRoll: -0.052,          // 軀幹反向補 45%，脊椎的 S 形
  torsoPitch: 0.14,           // 前傾 8°
  headYaw: -0.22,             // 頭獨立轉向武器側
  headPitch: 0.04,
  armPitch: -0.12, armYaw: -0.26, armRoll: -0.22,   // 右臂外張舉起
  aimConverge: 0.17,          // 前臂再往內收，槍口交匯
  pylonRoll: -0.10, pylonYaw: 0.08,
  forePitch: -1.30,          // 前臂舉角（槍要平舉）
  stride: 0.30, strideK: 0.66 // 步幅：腿長的機體跨得開
};

export var LEG_L1 = 1.02, LEG_L2 = 0.95, LEG_FOOT = 0.165;
/** `bodyY` 是站立時髖在世界的高度（未套用 PROP.scale）。 */
export var STANCE = { bodyY: 2.00, ankle: 0.165, stance: 0.48 };

/** CHASSIS-0 的骨架參數。`knee: 1` = 逆關節（膝朝前）。 */
export var IK_C0 = { l1: LEG_L1, l2: LEG_L2, foot: LEG_FOOT, knee: 1 };

export function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
export function wrapAng(a) {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
/** 與幀率無關的指數逼近。lambda 越大越快。 */
export function dampTo(cur, tgt, lambda, dt) { return tgt + (cur - tgt) * Math.exp(-lambda * dt); }

/** 臨界阻尼彈簧。`to()` 每幀逼近目標，`kick()` 直接給速度（後座、落地）。 */
export function Spring(this: any, k, d, x?) { this.k = k; this.d = d; this.x = x || 0; this.v = 0; }
/* **大 dt 一定要切子步。** 這是顯式歐拉，dt 超過約 2/√k 就發散 ——
   最硬的後座彈簧（k=230）門檻只有 0.13 秒，也就是低於 8fps 就會炸：
   實測 headless（軟體渲染，幀時間 105–180ms）機體的髖高會在兩秒內
   衝到 10 萬，整台飛出畫面。玩家端同型的觸發條件是切分頁回來那一幀
   （`tick` 把 real 夾在 0.25，仍遠超門檻）。
   60fps 的 0.0167 不切（n = 1），數值與原本逐字相同 —— 零回歸。 */
Spring.prototype.to = function (t, dt) {
  var n = dt > 0.02 ? Math.min(8, Math.ceil(dt / 0.02)) : 1, h = dt / n;
  for (var i = 0; i < n; i++) {
    this.v += (t - this.x) * this.k * h - this.v * this.d * h;
    this.x += this.v * h;
  }
  return this.x;
};
Spring.prototype.kick = function (v) { this.v += v; return this; };

/** 平面 2 連桿 IK。給「踝相對髖的位移」，回傳大腿與膝的角度。

    `cfg` 省略時就是 CHASSIS-0 的逆關節（膝朝前）。`cfg.knee = -1` 取
    **另一支解**，膝往後折 —— 那是 LANCER-2 的正關節腿。兩支解的差別
    只在 `a` 的正負：`hip = b ∓ a`、`knee = ±(a+c)`，其餘完全共用，
    所以兩台機體的腳永遠貼地這件事只需要驗一次。

    **腳的位置是輸入、關節角是輸出。** 反過來做（手調角度）只要一動就穿地。
    d 先夾在可達範圍內，所以 IK 永遠有解、永遠不會 NaN。 */
export function legIK(dy, dz, cfg?) {
  var l1 = cfg ? cfg.l1 : LEG_L1, l2 = cfg ? cfg.l2 : LEG_L2;
  var k = cfg && cfg.knee < 0 ? -1 : 1;
  var d = clamp(Math.sqrt(dy * dy + dz * dz), Math.abs(l1 - l2) + 0.03, l1 + l2 - 0.03);
  var b = Math.atan2(-dz, -dy);
  var a = Math.acos(clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1));
  var c = Math.acos(clamp((l2 * l2 + d * d - l1 * l1) / (2 * l2 * d), -1, 1));
  return { hip: b - k * a, knee: k * (a + c) };
}
/** 正解：給關節角，回傳髖到腳底的垂直距離。用來驗 IK（往返誤差要是 0）。 */
export function legDrop(q, cfg?) {
  var l1 = cfg ? cfg.l1 : LEG_L1, l2 = cfg ? cfg.l2 : LEG_L2, ft = cfg ? cfg.foot : LEG_FOOT;
  return l1 * Math.cos(q.hip) + l2 * Math.cos(q.hip + q.knee) + ft;
}
